(() => {
  const recommendationSelector = '[data-pc-cart-recommendations]';
  const addButtonSelector = '[data-pc-cart-add]';

  function routesRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function parseIds(value) {
    return [...new Set(String(value || '').split(',').map((id) => id.trim()).filter(Boolean))];
  }

  function formatMoney(cents, currency, locale) {
    const value = Number(cents || 0) / 100;

    try {
      return new Intl.NumberFormat(locale || 'de-DE', {
        style: 'currency',
        currency: currency || 'EUR',
      }).format(value);
    } catch (_error) {
      return `${value.toFixed(2).replace('.', ',')} €`;
    }
  }

  function productImage(product) {
    const featuredImage = product.featured_image || product.images?.[0];

    if (typeof featuredImage === 'string') return featuredImage;
    return featuredImage?.url || featuredImage?.src || '';
  }

  function firstAvailableVariant(product) {
    return product.variants?.find((variant) => variant.available) || product.variants?.[0];
  }

  function plainText(value) {
    const temporaryElement = document.createElement('div');
    temporaryElement.innerHTML = value || '';
    return temporaryElement.textContent?.replace(/\s+/g, ' ').trim() || '';
  }

  function recommendationCard(product, options) {
    const variant = firstAvailableVariant(product);
    if (!variant) return null;

    const article = document.createElement('article');
    article.className = 'pc-cart-recommendation';
    article.dataset.productId = product.id;

    const imageLink = document.createElement('a');
    imageLink.className = 'pc-cart-recommendation__image-link';
    imageLink.href = product.url || `${routesRoot()}products/${product.handle}`;
    imageLink.setAttribute('aria-label', product.title);

    const imageUrl = productImage(product);
    if (imageUrl) {
      const image = document.createElement('img');
      image.className = 'pc-cart-recommendation__image';
      image.src = imageUrl;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 152;
      image.height = 152;
      imageLink.append(image);
    }

    const content = document.createElement('div');
    content.className = 'pc-cart-recommendation__content';

    const heading = document.createElement('h4');
    const productLink = document.createElement('a');
    productLink.href = imageLink.href;
    productLink.textContent = product.title;
    heading.append(productLink);

    const description = document.createElement('p');
    const descriptionText = plainText(product.description);
    description.textContent =
      descriptionText.length > 78 ? `${descriptionText.slice(0, 75).trim()}…` : descriptionText;

    const price = document.createElement('div');
    price.className = 'pc-cart-recommendation__price';
    const currentPrice = Number(variant.price ?? product.price ?? 0);
    const compareAtPrice = Number(variant.compare_at_price ?? product.compare_at_price ?? 0);

    if (compareAtPrice > currentPrice) {
      const oldPrice = document.createElement('s');
      oldPrice.textContent = formatMoney(compareAtPrice, options.currency, options.locale);
      price.append(oldPrice);
    }

    const currentPriceElement = document.createElement('strong');
    currentPriceElement.textContent = formatMoney(currentPrice, options.currency, options.locale);
    price.append(currentPriceElement);

    content.append(heading);
    if (description.textContent) content.append(description);
    content.append(price);

    const addButton = document.createElement('button');
    addButton.className = 'pc-cart-recommendation__add';
    addButton.type = 'button';
    addButton.dataset.pcCartAdd = variant.id;
    addButton.setAttribute('aria-label', `${product.title} zum Warenkorb hinzufügen`);

    const plus = document.createElement('span');
    plus.setAttribute('aria-hidden', 'true');
    plus.textContent = '+';
    addButton.append(plus);

    article.append(imageLink, content, addButton);
    return article;
  }

  async function fetchRecommendations(productId) {
    const requestUrl = new URL(`${routesRoot()}recommendations/products.json`, window.location.origin);
    requestUrl.searchParams.set('product_id', productId);
    requestUrl.searchParams.set('limit', '6');
    requestUrl.searchParams.set('intent', 'related');

    const response = await fetch(requestUrl.toString(), {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error('Produktempfehlungen konnten nicht geladen werden.');
    const responseData = await response.json();
    return responseData.products || [];
  }

  async function loadRecommendations(container) {
    if (container.dataset.pcCartRecommendationsInitialized === 'true') return;
    container.dataset.pcCartRecommendationsInitialized = 'true';

    const list = container.querySelector('[data-pc-cart-recommendations-list]');
    const status = container.querySelector('[data-pc-cart-recommendations-status]');
    const sourceProductIds = parseIds(container.dataset.sourceProductIds);
    const cartProductIds = new Set(parseIds(container.dataset.cartProductIds));
    const limit = Math.max(1, Number.parseInt(container.dataset.limit, 10) || 3);
    const options = {
      currency: container.dataset.currency || 'EUR',
      locale: container.dataset.locale || 'de-DE',
    };

    if (!list || sourceProductIds.length === 0) return;

    try {
      const recommendations = [];
      const addedProductIds = new Set();

      for (const productId of sourceProductIds) {
        const products = await fetchRecommendations(productId);

        products.forEach((product) => {
          const normalizedId = String(product.id);
          if (
            recommendations.length >= limit ||
            cartProductIds.has(normalizedId) ||
            addedProductIds.has(normalizedId) ||
            !product.available
          ) {
            return;
          }

          addedProductIds.add(normalizedId);
          recommendations.push(product);
        });

        if (recommendations.length >= limit) break;
      }

      if (recommendations.length > 0) {
        const cards = recommendations
          .map((product) => recommendationCard(product, options))
          .filter(Boolean);

        if (cards.length > 0) list.replaceChildren(...cards);
      }
    } catch (_error) {
      if (status) status.textContent = 'Die Bestseller werden als Empfehlungen angezeigt.';
    }

    if (!list.children.length) container.hidden = true;
  }

  async function addRecommendation(button) {
    if (button.disabled) return;

    const status = button
      .closest(recommendationSelector)
      ?.querySelector('[data-pc-cart-recommendations-status]');
    const originalText = button.querySelector('span')?.textContent || '+';

    button.disabled = true;
    button.classList.add('is-loading');
    if (button.querySelector('span')) button.querySelector('span').textContent = '…';

    try {
      const response = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(button.dataset.pcCartAdd),
              quantity: 1,
            },
          ],
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      });

      const responseData = await response.json();
      if (!response.ok) throw new Error(responseData.description || 'Das Produkt konnte nicht hinzugefügt werden.');

      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer?.renderContents && responseData.sections) {
        cartDrawer.renderContents(responseData);
      } else {
        window.location.reload();
      }
    } catch (error) {
      button.disabled = false;
      button.classList.remove('is-loading');
      if (button.querySelector('span')) button.querySelector('span').textContent = originalText;
      if (status) status.textContent = error.message;
    }
  }

  function initializeRecommendations(root = document) {
    root.querySelectorAll(recommendationSelector).forEach((container) => loadRecommendations(container));
  }

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest(addButtonSelector);
    if (!addButton) return;
    addRecommendation(addButton);
  });

  function start() {
    initializeRecommendations();

    const observer = new MutationObserver(() => initializeRecommendations());
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => initializeRecommendations(event.target));
})();
